import * as React from 'react';

import { dateStringToUTCStart } from '@rematter/common/dateTime';
import {
  CurrencyCode,
  enumQuoteType,
  FreightTerm,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DialogClosingConfirmation,
  EntityDetails,
  Loading,
  LocalizedDate,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideArrowLeft } from 'lucide-react';
import { useLocation } from 'wouter';

import * as state from '../../_state/quoteDetail.state';
import {
  $discardBaseline,
  $hasUnsavedChanges,
  $resetQuoteFormToQuote,
} from '../../_state/quoteForm.state';
import { usePopulateQuoteForm } from '../../hooks/usePopulateQuoteForm';
import { useTranslations } from '../../i18n/useTranslations';
import { brokerageRoutes } from '../../index';
import { QuoteFormState } from '../../types/quote';
import { colorByQuoteStatus } from '../../utils/status';
import { BrokerageLayout } from '../_layouts/BrokerageLayout';

import { QuoteCloneDialog } from './QuoteCloneDialog';
import { QuoteDetailHeaderActions } from './QuoteDetailHeaderActions';
import { QuoteTabMaterials } from './QuoteTabMaterials';
import { QuoteTabNotes } from './QuoteTabNotes';
import { QuoteTabOverview } from './QuoteTabOverview';

import { ModuleLayout } from '~/shared/layouts/ModuleLayout';

type QuoteTab = 'overview' | 'lineItems' | 'notes';

//
// ⚛️ ⚛️ ⚛️ QUOTE DETAIL VIEW ⚛️ ⚛️ ⚛️
//
export const QuoteDetailView = () => {
  const t = useTranslations();
  const [, navigate] = useLocation();

  // States
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<QuoteTab | null>(null);

  // Atoms
  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);
  const { data: quote, refetch } = useAtomValue(state.$remoteQuoteDetail);
  const isLoading = useAtomValue(state.$isQuoteLoading);
  const error = useAtomValue(state.$quoteError);
  const [discardBaseline, setDiscardBaseline] = useAtom($discardBaseline);
  const [tab, setTab] = useAtom(state.$quoteTab);
  const resetForm = useSetAtom($resetQuoteFormToQuote);
  const updateQuote = useSetAtom(state.$updateQuote);

  // Other data hooks
  const toast = useToast();
  usePopulateQuoteForm({ quote });

  const activeTab = tab || 'overview';

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(brokerageRoutes.quotes.path);
  }, [navigate]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.quotes.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: QuoteFormState) => {
      if (!quote?.id || !data) return;

      try {
        setIsUpdating(true);
        await updateQuote({
          id: quote.id,
          accountId: data.accountId,
          facilityId: data.facilityId ?? null,
          validUntil: dateStringToUTCStart(data.validUntil),
          expectedShipDate: dateStringToUTCStart(data.expectedShipDate),
          paymentTerm: (data.paymentTerm as PaymentTerm) || null,
          paymentCurrency: (data.paymentCurrency as CurrencyCode) || null,
          freightTerm: (data.freightTerm as FreightTerm) || null,
          freightCoveredByCustomer: data.freightCoveredByCustomer ?? null,
          notes: data.notes.trim() || null,
        });

        setDiscardBaseline(data);
        toast.success(t.quotes.toast.updated);
      } catch (err) {
        toast.error(
          t.quotes.toast.updateError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [quote?.id, updateQuote, setDiscardBaseline, toast, t],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (hasUnsavedChanges) {
        setPendingTabChange(newTab as QuoteTab);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm, setTab]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  if (isLoading) {
    return (
      <ModuleLayout title={t.quotes.detail.title}>
        <div className="p-6">
          <Loading />
        </div>
      </ModuleLayout>
    );
  }

  if (error || !quote) {
    return (
      <ModuleLayout title={t.quotes.detail.title}>
        <div className="p-6">
          <div className="text-failure">
            {t.common.errors.notFound(t.quotes.detail.title)}
          </div>
          <Button variant="outline" onPress={handleBack} className="mt-4">
            <LucideArrowLeft className="mr-2 size-4" />
            {t.quotes.detail.backButton}
          </Button>
        </div>
      </ModuleLayout>
    );
  }

  const titleType =
    quote.type === enumQuoteType.Purchase
      ? t.quotes.detail.purchaseOrder
      : t.quotes.detail.salesOrder;

  return (
    <BrokerageLayout className="@QuoteDetailView">
      <EntityDetails
        onChangeTab={(newTab) => setTab(newTab as QuoteTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={activeTab}
        onBackPressed={handleBack}
        primaryTitle={t.quotes.detail.quoteTitle(titleType, quote.quoteNumber)}
        subtitle={
          <>
            {t.common.labels.created}{' '}
            <LocalizedDate date={quote.timestamps.createdAt} />
          </>
        }
        badges={
          <>
            <Badge
              color={
                quote.type === enumQuoteType.Purchase ? 'warning' : 'success'
              }
              size="small"
            >
              {quote.type === enumQuoteType.Purchase
                ? t.quotes.types.purchaseShort
                : t.quotes.types.salesShort}
            </Badge>
            <Badge color={colorByQuoteStatus[quote.status]} size="small">
              {
                t.common.status[
                  quote.status.toLowerCase() as keyof typeof t.common.status
                ]
              }
            </Badge>
          </>
        }
        headerActions={<QuoteDetailHeaderActions />}
        tabs={{
          overview: {
            title: t.quotes.detail.tabs.overview,
            content: () => (
              <QuoteTabOverview
                quote={quote}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          lineItems: {
            title: t.quotes.detail.tabs.lineItems,
            content: () => (
              <QuoteTabMaterials quote={quote} onRefetch={refetch} />
            ),
          },
          notes: {
            title: t.quotes.detail.tabs.notes,
            content: () => (
              <QuoteTabNotes
                quote={quote}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
        }}
      />
      <QuoteCloneDialog />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.quotes.detail.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </BrokerageLayout>
  );
};
